import React, { useRef, useLayoutEffect } from "react";
import { Bot, RefreshCw, Send } from "lucide-react";

interface ThreadInputBarProps {
  inputText: string;
  placeholder?: string;
  sending: boolean;
  isWaitingForAgent: boolean;
  hasComments?: boolean;
  onInputChange: (text: string) => void;
  onSendMessage: (e: React.FormEvent) => void;
}

export function ThreadInputBar({
  inputText,
  placeholder,
  sending,
  isWaitingForAgent,
  hasComments: _hasComments,
  onInputChange,
  onSendMessage,
}: ThreadInputBarProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea height to accommodate multiline text up to 5-6 lines
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [inputText]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (inputText.trim() && !sending && !isWaitingForAgent) {
        formRef.current?.requestSubmit();
      }
    }
  };

  return (
    <footer className="shrink-0 w-full px-4 md:px-6 py-3 bg-stone-50 dark:bg-stone-950 border-t border-stone-200/50 dark:border-stone-800/50">
      <div className="w-full max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <form
              ref={formRef}
              onSubmit={onSendMessage}
              className="flex items-end gap-2 px-4 py-2 rounded-2xl bg-stone-100 dark:bg-[#1e1f20] border border-stone-300 dark:border-stone-800 focus-within:border-stone-400 dark:focus-within:border-stone-600 transition shadow-inner"
            >
              <Bot className="w-4 h-4 text-stone-400 shrink-0 mb-1.5" />
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder={placeholder || "Send message to agent..."}
                value={inputText}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending || isWaitingForAgent}
                className="flex-1 bg-transparent px-2 py-1 text-sm text-stone-900 dark:text-stone-100 placeholder:text-stone-500 focus:outline-hidden resize-none min-h-[28px] max-h-[140px] leading-5 overflow-y-auto"
              />
              <button
                type="submit"
                disabled={!inputText.trim() || sending || isWaitingForAgent}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-medium hover:bg-stone-800 dark:hover:bg-white transition disabled:opacity-30 cursor-pointer shadow-2xs shrink-0 mb-0.5"
              >
                {sending ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                <span>Send</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </footer>
  );
}

